import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../contexts/AppContext';
import { Upload as UploadIcon, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';

const Upload = () => {
  const { uploadDocument, subscription, usage } = useApp();
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const maxFileSize = subscription?.plan_type === 'FREE' ? 5 * 1024 * 1024 : 
                     subscription?.plan_type === 'PRO' ? 50 * 1024 * 1024 : 
                     500 * 1024 * 1024;

  const documentsLimit = subscription?.plan_type === 'FREE' ? 5 : 
                        subscription?.plan_type === 'PRO' ? 100 : 
                        Infinity;

  const documentsUsed = usage?.documents_used ?? 0;
  const effectiveDocumentsLimit = usage?.documents_limit ?? documentsLimit;
  const canUpload = effectiveDocumentsLimit === null || documentsUsed < effectiveDocumentsLimit;

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach((error) => {
        if (error.code === 'file-too-large') {
          toast.error(`${file.name} is too large. Max size: ${Math.round(maxFileSize / 1024 / 1024)}MB`);
        } else if (error.code === 'file-invalid-type') {
          toast.error(`${file.name} is not a supported file type.`);
        }
      });
    });

    if (!canUpload) {
      toast.error(`Document limit reached (${effectiveDocumentsLimit === null ? '∞' : effectiveDocumentsLimit}). Please upgrade your plan.`);
      return;
    }

    // Process accepted files
    for (const file of acceptedFiles) {
      try {
        setUploading(true);
        const result = await uploadDocument(file);
        setUploadedFiles(prev => [...prev, { file, result, status: 'success' }]);
        toast.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadedFiles(prev => [...prev, { file, error: error.response?.data?.detail || 'Upload failed', status: 'error' }]);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  }, [uploadDocument, canUpload, effectiveDocumentsLimit, maxFileSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md']
    },
    maxSize: maxFileSize,
    disabled: uploading || !canUpload
  });

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Upload Documents</h1>
        <p className="text-gray-600 mt-2">
          Upload your documents to start analyzing and chatting with them using AI.
        </p>
      </div>

      {/* Usage Stats */}
      <div className="card mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Current Usage</h3>
            <p className="text-sm text-gray-600 mt-1">
              You've used {documentsUsed} of {effectiveDocumentsLimit === null || effectiveDocumentsLimit === Infinity ? '∞' : effectiveDocumentsLimit} documents
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {documentsUsed} / {effectiveDocumentsLimit === null || effectiveDocumentsLimit === Infinity ? '∞' : effectiveDocumentsLimit}
            </div>
            <div className="text-sm text-gray-500">
              Max file size: {Math.round(maxFileSize / 1024 / 1024)}MB
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        {effectiveDocumentsLimit !== Infinity && effectiveDocumentsLimit !== null && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min((documentsUsed / effectiveDocumentsLimit) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div className="card mb-8">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
            isDragActive
              ? 'border-purple-400 bg-purple-50'
              : canUpload
              ? 'border-gray-300 hover:border-purple-400 hover:bg-purple-50 cursor-pointer'
              : 'border-gray-200 bg-gray-50 cursor-not-allowed'
          }`}
        >
          <input {...getInputProps()} />
          
          <div className="space-y-4">
            <div className={`mx-auto w-16 h-16 flex items-center justify-center rounded-full ${
              canUpload ? 'bg-purple-100' : 'bg-gray-100'
            }`}>
              <UploadIcon className={`h-8 w-8 ${canUpload ? 'text-purple-600' : 'text-gray-400'}`} />
            </div>
            
            {canUpload ? (
              <>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
                  </p>
                  <p className="text-gray-600">or click to browse</p>
                </div>
                
                <div className="text-sm text-gray-500">
                  <p>Supported formats: PDF, DOCX, DOC, TXT, MD</p>
                  <p>Maximum file size: {Math.round(maxFileSize / 1024 / 1024)}MB</p>
                </div>
              </>
            ) : (
              <div>
                <p className="text-lg font-medium text-gray-600">
                  Upload limit reached
                </p>
                <p className="text-gray-500">
                  You've reached your plan's document limit. 
                  <a href="/subscription" className="text-purple-600 hover:text-purple-700 ml-1">
                    Upgrade your plan
                  </a> to upload more documents.
                </p>
              </div>
            )}
          </div>
        </div>

        {uploading && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-blue-700 font-medium">Processing uploads...</span>
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Uploads</h3>
          <div className="space-y-3">
            {uploadedFiles.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    item.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {item.status === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.file.name}</p>
                    <p className="text-sm text-gray-500">
                      {Math.round(item.file.size / 1024)} KB
                    </p>
                    {item.status === 'error' && (
                      <p className="text-sm text-red-600">{item.error}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeUploadedFile(index)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-8 card bg-blue-50 border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Upload Tips</h3>
        <ul className="space-y-2 text-blue-800 text-sm">
          <li>• Ensure your documents are text-based for best AI analysis results</li>
          <li>• PDFs with images will be processed, but text content works better</li>
          <li>• Larger documents may take longer to process</li>
          <li>• You can upload multiple files at once</li>
        </ul>
      </div>
    </div>
  );
};

export default Upload;
